import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Button } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { LegalFormalityDocumentContext } from 'contexts/LegalFormalityDocumentContext'
import { AbilityContext } from 'contexts/abilityContext'
import Checks from './Checks'
import SideDetails from './SideDetails'
import Uploads from './Uploads'
import MarkdownEditorModal from './MarkdownEditorModal'
import AiValidationResult from './AiValidationResult'
import { toDotSeparated } from './index'
import InternalNotes from '../Form/InternalNotes'
import { CurrentLegalFormalityContext } from 'contexts/LegalFormalityContext'

const Details = () => {
  const { t } = useTranslation()
  const { legalFormality } = useContext(CurrentLegalFormalityContext)
  const { selectedDocumentListItem, currentDocument } = useContext(
    LegalFormalityDocumentContext
  )
  const ability: any = useContext(AbilityContext)
  const formalistOnly = ability.can('update', 'LegalFormality')

  const [checklistTop, setChecklistTop] = useState(0)
  const [markdownModalOpen, setMarkdownModalOpen] = useState(false)

  useEffect(() => {
    updateChecklistTop()
  }, [selectedDocumentListItem])

  useEffect(() => {
    if (!checklistTop) return

    window.addEventListener('scroll', isSticky)
    return () => {
      window.removeEventListener('scroll', isSticky)
    }
  }, [checklistTop])

  const isSticky = (e: any) => {
    const checklistEl = document && document.querySelector('.sticky-column')
    if (!checklistEl) return

    const scrollTop = window.scrollY
    if (scrollTop >= checklistTop - 10) {
      checklistEl.classList.add('is-sticky')
    } else {
      checklistEl.classList.remove('is-sticky')
    }
  }

  const updateChecklistTop = () => {
    const instantScrollY = window.scrollY
    const checklistEl = document && document.querySelector('.sticky-column')
    if (!checklistEl) return 0

    const checklistReactEl = checklistEl.getBoundingClientRect()
    const checklistTop = checklistReactEl.top

    const absoluteY = instantScrollY + checklistTop
    setChecklistTop(absoluteY)
  }

  if (!selectedDocumentListItem) return <></>

  const doc = currentDocument
  const targetLabel =
    selectedDocumentListItem.target &&
    selectedDocumentListItem.target?.label &&
    `: ${selectedDocumentListItem.target.label || ''}`

  const tagsDisplay =
    selectedDocumentListItem.tags && selectedDocumentListItem.tags.join(' - ')
  const label = `
    ${t(
      `legalFormalities:document_types.${toDotSeparated(
        selectedDocumentListItem.documentType
      )}`
    )}
    ${selectedDocumentListItem.required ? ' *' : ''}
    ${targetLabel ? ` ${targetLabel}` : ''}
    ${tagsDisplay ? ` (${tagsDisplay})` : ''}
  `

  if (doc == undefined) {
    return (
      <DocumentWrapperEmpty>
        <Title>
          {t('legalFormalities:show.misc.viewing')}
          {': '}
          {label}
        </Title>
        <Uploads />
      </DocumentWrapperEmpty>
    )
  }

  return (
    <Wrapper>
      <Title>
        {t('legalFormalities:show.misc.viewing')}
        {': '}
        {label}
        {` - ( doc_id: ${doc.id})`}
      </Title>
      <DocumentActions>
        <Uploads />
      </DocumentActions>
      <DocumentWrapper>
        <DocumentSideDetailsWrapper>
          {doc && doc.fileRecto && <SideDetails side="recto" document={doc} />}
          {doc && doc.fileVerso && <SideDetails side="verso" document={doc} />}
        </DocumentSideDetailsWrapper>
        <DocumentSideActionsWrapper>
          <StickyColumn className="sticky-column">
            {formalistOnly && legalFormality?.aiValidationResponse && (
              <AiValidationResult />
            )}
            {formalistOnly &&
              doc?.markdownContent &&
              doc.markdownContent.length > 0 && (
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => setMarkdownModalOpen(true)}
                >
                  {t('legalFormalities:show.misc.editMarkdown', {
                    defaultValue: 'Éditer le markdown',
                  })}
                </Button>
              )}
            {formalistOnly && <div>{doc && <Checks />}</div>}
            <InternalNotes />
          </StickyColumn>
          <MarkdownEditorModal
            open={markdownModalOpen}
            onClose={() => setMarkdownModalOpen(false)}
          />
        </DocumentSideActionsWrapper>
      </DocumentWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  margin-top: 25px;
`

const DocumentWrapperEmpty = styled.div`
  margin-top: 15px;
  display: flex;
  flex-flow: column;
  gap: 10px;
`

const DocumentWrapper = styled.div`
  margin-top: 15px;
  display: flex;
  flex-flow: row nowrap;
  gap: 20px;
  justify-content: space-between;
`

const DocumentActions = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const DocumentSideActionsWrapper = styled.div`
  width: 400px;
  display: flex;
  flex-flow: column;
  position: relative;
  flex-shrink: 0;
`

const StickyColumn = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: 40px;
  width: 400px;

  &.is-sticky {
    position: fixed;
    top: 0;
    right: 40px;
    max-height: calc(100vh - 150px); // don't overflow bottom buttons
    padding: 10px 0;
    width: 400px; // add paddings
    z-index: 999;
    animation: 500ms ease-in-out 0s normal none 1 running fadeInDown;
    background-color: white;
  }
`

const Title = styled.div`
  font-weight: bold;
  font-size: 16px;
`

const DocumentSideDetailsWrapper = styled.div`
  display: flex;
  flex-flow: column;
  gap: 10px;
  flex-grow: 1;
  max-width: 800px;
`

export default Details
